import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api/client.js';
import { TableSkeleton, ErrorBanner, Badge, CardHead, Empty } from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { RegistrationStatus } from './ExtensionTabs.js';
import { relativeTime, formatDuration, formatMoney } from '../../lib/format.js';
import type { Extension } from '@arcnet/contracts';

/**
 * Why isn't this extension working?
 *
 * Built for the moment somebody rings support and says "my phone doesn't work",
 * which is never one question. It is: is the handset even registered, is the
 * seat allowed to dial what they tried, did the calls actually happen, and is
 * anything about this seat's configuration self-contradictory.
 *
 * Answering those from the other tabs means opening five and holding the
 * answers in your head. Nothing here is new data -- it is the four endpoints
 * that already existed, arranged around a question instead of around a table.
 *
 * Registration is here too -- whether the handset is registered, from where,
 * and the sign-ins that failed -- because "is it even registered" is the first
 * of those questions. The handset card this screen used to carry was a shorter
 * copy of the same facts.
 *
 * READ-ONLY on purpose. A diagnostic screen that also changes things invites
 * changing something before the fault is understood, and the section you would
 * fix it in is one click away: each problem links to it.
 */

interface Feature { key: string; name: string; enabled: boolean; from_type: boolean }
interface PolicyTest {
  destination: string; allowed: boolean; reason: string; explanation: string;
}
interface Cdr {
  id: string; direction: string; from_number: string | null; to_number: string | null;
  started_at: string; answered_at: string | null; duration_sec: number;
  billable_sec: number | null; sell_amount: string | null; currency: string | null;
  hangup_cause: string | null;
}

/** The sections a problem found here is fixed in. */
export type FixView = 'phone' | 'details' | 'forwarding' | 'restrictions' | 'voicemail';

export function ExtensionTroubleshoot({ tenantId, ext, open = {} }: {
  tenantId: string; ext: Extension;
  /**
   * Opens the section that fixes a problem. Only the sections this console has
   * are passed, so a problem fixed in the customer's own portal is still named,
   * without a link that goes nowhere.
   */
  open?: Partial<Record<FixView, () => void>>;
}) {
  const [testNumber, setTestNumber] = useState('');

  const calls = useQuery({
    queryKey: ['cdrs', tenantId, ext.id, 'troubleshoot'],
    queryFn: () => api.get<{ data: Cdr[] }>(
      `/v1/tenants/${tenantId}/cdrs?extension_id=${ext.id}&limit=10`),
  });
  const features = useQuery({
    queryKey: ['tenants', tenantId, 'extensions', ext.id, 'features'],
    queryFn: () => api.get<{ data: Feature[]; extension_type_name: string | null }>(
      `/v1/tenants/${tenantId}/extensions/${ext.id}/features`),
  });
  /**
   * Asked directly, because the extension DTO does not carry voicemail_box_id.
   * `retry: false` -- a seat with no mailbox 404s, and that 404 IS the answer
   * this screen wants rather than a failure to retry.
   */
  const mailbox = useQuery({
    queryKey: ['voicemail', tenantId, ext.id],
    queryFn: () => api.get<{ id: string }>(
      `/v1/tenants/${tenantId}/extensions/${ext.id}/voicemail`),
    retry: false,
  });

  const policy = useQuery({
    queryKey: ['dial-policy', tenantId, ext.id, testNumber],
    queryFn: () => api.get<{ resolved: unknown; test?: PolicyTest }>(
      `/v1/tenants/${tenantId}/extensions/${ext.id}/dial-policy`
      + (testNumber ? `?test=${encodeURIComponent(testNumber.replace(/\D/g, ''))}` : '')),
    enabled: testNumber.replace(/\D/g, '').length === 0 || testNumber.replace(/\D/g, '').length >= 3,
  });

  const reg = ext.registration;
  const enabled = new Set((features.data?.data ?? []).filter((f) => f.enabled).map((f) => f.key));

  /**
   * Contradictions, not a config dump.
   *
   * Each of these is a state the platform permits and which produces a
   * complaint that sounds like something else -- "calls go missing", "voicemail
   * never picks up". Listing what IS set would bury them; listing only what
   * disagrees with itself is the whole value of the screen.
   */
  const problems: Array<{ tone: 'danger' | 'warn'; text: string; where: string; fix?: FixView }> = [];

  if (!reg) {
    problems.push({ tone: 'danger', where: 'Phone setup', fix: 'phone',
      text: 'No handset has ever registered as this extension. It cannot receive calls at all.' });
  } else if (!reg.registered) {
    problems.push({ tone: 'danger', where: 'Phone setup', fix: 'phone',
      text: `The handset is offline${reg.last_seen_at ? `, last seen ${relativeTime(reg.last_seen_at)}` : ''}. Calls will not reach it.` });
  }

  if (ext.status !== 'active') {
    problems.push({ tone: 'danger', where: 'Details',
      text: `This seat is ${ext.status}, so it is excluded from the switch entirely — the handset cannot even register.` });
  }
  if (ext.do_not_disturb) {
    problems.push({ tone: 'warn', where: 'Details', fix: 'details',
      text: 'Do not disturb is on, so the handset will not ring. Often switched on from the phone and forgotten.' });
  }
  if (ext.forward_always) {
    problems.push({ tone: 'warn', where: 'Forwarding', fix: 'forwarding',
      text: `Every call is forwarded to ${ext.forward_always}, so this handset never rings.` });
  }
  if (ext.internal_only) {
    problems.push({ tone: 'warn', where: 'Dialling restrictions', fix: 'restrictions',
      text: 'Internal calls only — any outside number is refused before it reaches a carrier.' });
  }
  if (!ext.extension_type_id) {
    problems.push({ tone: 'warn', where: 'Details', fix: 'details',
      text: 'No extension type, so no feature is included by default and each has to be switched on individually.' });
  }
  /*
    A mailbox the caller can reach but the owner cannot: the seat records
    messages and there is no PIN, so *97 refuses. Silent, and it looks like
    voicemail is broken rather than unconfigured.
  */
  if (enabled.has('voicemail') && mailbox.isError) {
    problems.push({ tone: 'warn', where: 'Voicemail', fix: 'voicemail',
      text: 'Voicemail is enabled as a feature but no mailbox exists, so unanswered calls ring out instead of recording.' });
  }

  return (
    <>
      <div className="arc-card" style={{ marginBottom: 12 }}>
        <CardHead title="What is wrong" sub={<>{problems.length === 0 ? 'nothing obvious' : `${problems.length} to check`}</>} />
        <div className="arc-card-body">
          {problems.length === 0 ? (
            <div className="arc-row" style={{ gap: 8 }}>
              <Badge tone="ok"><span className="arc-dot" aria-hidden="true" />healthy</Badge>
              <span className="arc-subtle">
                Registered, active, no forwarding or DND, and its features are set.
                If calls are still failing, check the recent calls below for a
                hangup cause.
              </span>
            </div>
          ) : (
            <div className="arc-stack" style={{ gap: 8 }}>
              {problems.map((p, i) => {
                const go = p.fix ? open[p.fix] : undefined;
                return (
                  <div key={i} className="arc-row" style={{ gap: 9, alignItems: 'flex-start' }}>
                    <Badge tone={p.tone === 'danger' ? 'danger' : 'warn'}>{p.where}</Badge>
                    <span style={{ flex: 1 }}>{p.text}</span>
                    {go && (
                      <button type="button" className="arc-btn arc-btn-ghost arc-btn-sm"
                        onClick={go} aria-label={`Open ${p.where}`}>
                        Open <Icon name="chevronRight" size={12} />
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <RegistrationStatus tenantId={tenantId} ext={ext} />

      <div className="arc-card" style={{ marginBottom: 12 }}>
        <CardHead title="Can it dial…" />
        <div className="arc-card-body">
          {/*
            The SAME resolver the switch uses, asked the same question. So an
            answer here is the answer a real call would get -- which is the
            only reason a test like this is worth having.
          */}
          <input className="arc-input arc-mono" value={testNumber} inputMode="tel"
            placeholder="+27 82 123 4567" style={{ maxWidth: 260 }}
            aria-label="Number to test"
            onChange={(e) => setTestNumber(e.target.value)} />
          <div className="arc-hint" style={{ marginTop: 6 }}>
            Checked against this seat's resolved dialling policy — the same
            decision the switch makes.
          </div>
          {policy.data?.test && (
            <div className="arc-row" style={{ gap: 9, marginTop: 12 }}>
              {policy.data.test.allowed
                ? <Badge tone="ok">allowed</Badge>
                : <Badge tone="danger">refused</Badge>}
              <span>{policy.data.test.explanation}</span>
            </div>
          )}
          {!testNumber && (
            <p className="arc-subtle" style={{ marginTop: 12, marginBottom: 0 }}>
              Type the number they said failed.
            </p>
          )}
        </div>
      </div>

      <div className="arc-card">
        <CardHead title="Last 10 calls" sub="What actually happened" />
        {calls.data === undefined ? (
          <TableSkeleton query={calls} rows={5} cols={5} />
        ) : (calls.data?.data ?? []).length === 0 ? (
          <Empty icon="phone" title="No calls recorded for this extension"
            hint="If the customer says they have been calling, the calls are not reaching the platform at all — check registration above rather than routing." />
        ) : (
          <div className="arc-table-wrap">
            <table className="arc-table">
              <thead>
                <tr>
                  <th>When</th><th>Direction</th><th>Number</th>
                  <th>Outcome</th><th>Cost</th>
                </tr>
              </thead>
              <tbody>
                {(calls.data?.data ?? []).map((c) => (
                  <tr key={c.id}>
                    <td className="arc-subtle" style={{ whiteSpace: 'nowrap' }}>
                      {relativeTime(c.started_at)}
                    </td>
                    <td className="arc-subtle">{c.direction}</td>
                    <td className="arc-mono">
                      {c.direction === 'inbound' ? c.from_number : c.to_number}
                    </td>
                    <td>
                      {/*
                        The hangup cause, not a friendly word for it. NO_ANSWER,
                        USER_BUSY and CALL_REJECTED mean different things to
                        whoever is diagnosing, and translating them into "failed"
                        throws away the diagnosis.
                      */}
                      {c.answered_at
                        ? <span className="arc-row" style={{ gap: 6 }}>
                            <Badge tone="ok">answered</Badge>
                            <span className="arc-subtle">{formatDuration(c.duration_sec)}</span>
                          </span>
                        : <span className="arc-row" style={{ gap: 6 }}>
                            <Badge tone="warn">no answer</Badge>
                            {c.hangup_cause && (
                              <span className="arc-subtle arc-mono">{c.hangup_cause}</span>
                            )}
                          </span>}
                    </td>
                    <td className="arc-num arc-subtle">
                      {c.sell_amount ? formatMoney(c.sell_amount, c.currency ?? 'ZAR') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {(calls.error ?? features.error) != null && (
        <ErrorBanner error={calls.error ?? features.error} />
      )}
    </>
  );
}
